/**
 * R56-F3 — the reason the backend refused to quote this shipment.
 *
 * `/available-carriers` answers a refusal with a localized sentence in
 * `message`. The create-order form used to throw that away and show a fixed
 * "no service enabled, please activate a carrier" modal for EVERY failure —
 * so a merchant whose carriers were all switched on, shipping a parcel no
 * carrier serves, was sent to the carrier-toggles page to fix nothing.
 *
 * Returns undefined when there is nothing worth showing, which leaves the
 * caller on its previous generic copy rather than on a blank modal.
 *
 * 🔴 ONLY A *CODED* REFUSAL IS A SENTENCE WE MAY SHOW A MERCHANT.
 *
 * `ApiResponse` rewrites an uncaught server exception into the same envelope a
 * business refusal uses — `status: fail`, HTTP 400 — but with `code: 0`, an
 * empty `object`, and the raw exception text in `message`. Measured on staging
 * 2026-09-21: an XS parcel from Basra to six destinations answers
 * `{"status":"fail","code":0,"message":"Division by zero","object":[]}`.
 *
 * Reading `message` unconditionally therefore put the string "Division by zero"
 * in front of the merchant, in both languages — found by BOXY::QA rendering the
 * build rather than trusting the fix. A crash is not a refusal: it has no
 * remedy to offer, it leaks our internals, and the merchant cannot act on it.
 *
 * So the payload must IDENTIFY ITSELF as a business refusal: a Boxy error code
 * (six digits, e.g. 760001), or an `object.error_code`. Anything else falls
 * back to the caller's generic copy — which is wrong-ish, but wrong in a way
 * that is about us rather than a stack trace with the merchant's name on it.
 */

/** Boxy business codes are 6-digit; HTTP statuses and the crash sentinel 0 are not. */
const isBusinessCode = (code: unknown): boolean =>
  typeof code === 'number' && Number.isFinite(code) && code >= 100000;

export function carrierRefusalReason(error: unknown): string | undefined {
  const data = (
    error as {
      response?: { data?: { message?: unknown; code?: unknown; object?: unknown } };
    }
  )?.response?.data;

  const message = data?.message;
  if (typeof message !== 'string') return undefined;

  const trimmed = message.trim();
  if (trimmed === '') return undefined;

  const errorCode = (data?.object as { error_code?: unknown } | undefined)?.error_code;
  const coded = isBusinessCode(data?.code) || (typeof errorCode === 'string' && errorCode !== '');

  return coded ? trimmed : undefined;
}

/**
 * BOXY-301 — the refusal's machine-readable code, when the backend sent one.
 *
 * Threaded to NoCarrierModal as `reasonCode` for context. It does not gate
 * what the Boxy Select branch shows: owner decision 2026-09-28 made
 * `carrierRefusalReason` (business-coded refusals only) the single filter.
 */
export function carrierRefusalCode(error: unknown): string | undefined {
  const object = (error as { response?: { data?: { object?: RefusalObject } } })?.response?.data
    ?.object;
  const code = object?.error_code ?? object?.failed_orders?.[0]?.code;

  return typeof code === 'string' && code.trim() !== '' ? code.trim() : undefined;
}

type RefusalObject = {
  error_code?: unknown;
  service_code?: unknown;
  failed_orders?: { code?: unknown; error?: unknown; service_code?: unknown }[];
};

/**
 * BOXY-501 — MULTI_BOX_NEEDS_OTHER_SERVICE names the service that CAN carry
 * the boxes: the picker puts it on `object`, preview/create on the failed row.
 */
export function refusalServiceCode(error: unknown): string | undefined {
  const object = (error as { response?: { data?: { object?: RefusalObject } } })?.response?.data
    ?.object;
  const code = object?.service_code ?? object?.failed_orders?.[0]?.service_code;

  return typeof code === 'string' && code.trim() !== '' ? code.trim() : undefined;
}

/** The refusal sentence of preview/create's failed row (a 422 with a business code). */
export function failedRowRefusal(error: unknown): string | undefined {
  const row = (error as { response?: { data?: { object?: RefusalObject } } })?.response?.data
    ?.object?.failed_orders?.[0];
  const sentence = row?.error;

  return typeof row?.code === 'string' && typeof sentence === 'string' && sentence.trim() !== ''
    ? sentence.trim()
    : undefined;
}

const SERVICE_LANE_CODE = 'SERVICE_NO_ELIGIBLE_CARRIER';

/**
 * BOXY-588 — the order's service has no carrier for this address (Boxy Save to
 * a remote area). PATCH and create answer HTTP 422:
 *   { code: 'SERVICE_NO_ELIGIBLE_CARRIER', message,
 *     object: { failed_orders: [{ code: 'SERVICE_NO_ELIGIBLE_CARRIER', error }] } }
 * Returns the backend's sentence, or undefined for any other failure.
 */
export function serviceLaneRefusal(error: unknown): string | undefined {
  const data = (error as { response?: { data?: { code?: unknown; message?: unknown } } })?.response
    ?.data;
  if (data?.code !== SERVICE_LANE_CODE && carrierRefusalCode(error) !== SERVICE_LANE_CODE) {
    return undefined;
  }
  const message = typeof data?.message === 'string' ? data.message.trim() : '';
  return failedRowRefusal(error) ?? (message || undefined);
}
